import React from 'react';
import { AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react';
import { Input } from '../ui/Input';

const DOMAIN_SUBHEADINGS = {
  gm1: { title: 'Gross Motor Milestones', badge: 'Mobility & Posture' },
  fm1: { title: 'Fine Motor Coordination', badge: 'Hand & Finger Dexterity' },
  cm1: { title: 'Speech & Language Development', badge: 'Communication' },
  ss1: { title: 'Social & Emotional Engagement', badge: 'Interaction' },
  vs1: { title: 'Vision & Ocular Health', badge: 'Visual Screening' },
  hs1: { title: 'Hearing & Auditory Response', badge: 'Auditory Screening' },
  mh1: { title: 'Prenatal & Birth History', badge: 'Medical Background' },
  pc1: { title: 'Parent & Caregiver Observations', badge: 'Clinical Context' }
};

const QuestionRow = ({ label, name, options, value, onChange, error, index }) => {
  const isAnswered = value !== undefined && value !== '';

  return (
    <div className={`p-5 sm:p-6 rounded-2xl transition-all duration-200 border ${
      isAnswered 
        ? 'bg-white border-slate-200/80 shadow-sm' 
        : 'bg-slate-50/60 border-slate-100 hover:border-slate-200'
    }`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="flex items-start gap-3.5 max-w-xl">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5 transition-colors ${
            isAnswered 
              ? 'bg-emerald-100 text-emerald-700 font-extrabold' 
              : 'bg-slate-200 text-slate-600'
          }`}>
            {isAnswered ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : index}
          </span>
          <div>
            <p className="text-slate-800 font-semibold leading-relaxed text-sm sm:text-base">
              {label}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 sm:gap-3 pl-9 lg:pl-0">
          {options?.map((opt) => {
            const isSelected = value === opt;
            // Visual style variations based on option type
            const isNegativeAlert = (name === 'vs4' || name.startsWith('mh')) && opt === 'Yes';
            const isDelayAlert = name === 'hs5' && (opt === 'Significant' || opt === 'Mild');
            
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(name, opt)}
                className={`px-4 sm:px-5 py-2.5 rounded-xl text-xs font-bold tracking-wide transition-all border-2 flex items-center gap-1.5 active:scale-95 ${
                  isSelected 
                    ? isNegativeAlert || isDelayAlert
                      ? 'bg-amber-600 border-amber-600 text-white shadow-md ring-4 ring-amber-100'
                      : 'bg-indigo-600 border-indigo-600 text-white shadow-md ring-4 ring-indigo-100'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/40'
                }`}
              >
                {opt}
                {isSelected && <CheckCircle2 className="h-3.5 w-3.5 stroke-[2.5]" />}
              </button>
            );
          })}
        </div>
      </div>
      {error && (
        <p className="mt-3 text-xs text-red-600 flex items-center gap-2 font-bold bg-red-50 p-2.5 rounded-xl w-fit">
          <AlertCircle size={14} /> {error}
        </p>
      )}
    </div>
  );
};

export default function ScreeningAssessment({ section, answers, onChange, errors = {} }) {
  if (!section) return null;

  const totalQuestions = section.questions.length;
  const answeredCount = section.questions.filter(q => {
    if (q.type === 'textarea' || q.type === 'text') return answers[q.id]?.trim()?.length > 0;
    return answers[q.id] !== undefined && answers[q.id] !== '';
  }).length;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Section Header Card */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="h-2 w-2 rounded-full bg-indigo-600"></span>
              <span className="text-[11px] font-black uppercase tracking-widest text-indigo-600 font-outfit">
                Clinical Screening Protocol
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 font-outfit tracking-tight">
              {section.title}
            </h2>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/60 px-4 py-2.5 rounded-2xl shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Section Progress</p>
              <p className="text-sm font-black text-slate-800">
                <span className="text-indigo-600">{answeredCount}</span> of {totalQuestions} answered
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-xs text-indigo-600 shadow-sm">
              {Math.round((answeredCount / (totalQuestions || 1)) * 100)}%
            </div>
          </div>
        </div>

        {/* Question Rows with Group Subheadings */}
        <div className="space-y-4 pt-6">
          {section.questions.map((q, qIndex) => {
            const subheading = DOMAIN_SUBHEADINGS[q.id];

            return (
              <div key={q.id} className="space-y-3">
                {subheading && (
                  <div className="flex items-center justify-between pt-5 pb-1 border-t border-slate-100 first:border-0 first:pt-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 font-outfit">
                        {subheading.title}
                      </h4>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-full">
                      {subheading.badge}
                    </span>
                  </div>
                )}

                {q.type === 'textarea' || q.type === 'text' ? (
                  <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">
                        {qIndex + 1}
                      </span>
                      <label className="block text-slate-800 font-semibold text-sm sm:text-base leading-relaxed">
                        {q.label}
                      </label>
                    </div>

                    <div className="pl-9">
                      {q.type === 'textarea' ? (
                        <textarea
                          name={q.id}
                          value={answers[q.id] || ''}
                          onChange={(e) => onChange(q.id, e.target.value)}
                          className="w-full p-4 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none min-h-[110px] text-slate-700 text-sm font-medium transition-all placeholder:text-slate-300 bg-slate-50/50 focus:bg-white"
                          placeholder={q.placeholder || "Please provide any relevant details, observations, or timelines..."}
                        />
                      ) : (
                        <Input
                          name={q.id}
                          value={answers[q.id] || ''}
                          onChange={(e) => onChange(q.id, e.target.value)}
                          className="h-12 rounded-xl bg-slate-50/50 focus:bg-white border-slate-200 focus:border-indigo-500 text-sm font-medium"
                          placeholder="Provide details or description..."
                        />
                      )}
                      {errors[q.id] && (
                        <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5 font-bold">
                          <AlertCircle size={14} /> {errors[q.id]}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <QuestionRow
                    label={q.label}
                    name={q.id}
                    options={q.options}
                    value={answers[q.id]}
                    onChange={onChange}
                    error={errors[q.id]}
                    index={qIndex + 1}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
